import type { LogAttributes } from '@core/services/logging/logAttributes';
import { comparePatchId, type AttachmentSerializer, type NetPatchAttachmentManifest } from '@archipad-js/core/patch';
import { PatchAttachmentDescriptor } from '@archipad/services/file/patchFile';
import { TempPatch } from '@archipad/backend/archiweb/synchroDriver';

/**
 * Pure helpers on the local temp patch (`current`): no I/O, no sync state, never throw.
 * The reading with integrity checks and state update stays in the project manager (`_getTempPatch`).
 */

export const TEMP_PATCH_VERSION = 1;

export type TempPatchProblem = 'empty_patch_set' | 'wrong_version';

/**
 * @returns The first problem making the temp patch unusable, `null` if it is valid (or absent)
 */
export function validateTempPatch(tempPatch: TempPatch | null): TempPatchProblem | null {
    if (!tempPatch) {
        return null;
    }
    if (!tempPatch.patchSet?.length) {
        return 'empty_patch_set';
    }
    if (tempPatch.version != TEMP_PATCH_VERSION) {
        return 'wrong_version';
    }
    return null;
}

/**
 * Counts the entity operations of a `NetPatchData` (`[header, [entityType, ...operations], ...]`).
 */
export function countPatchOperations(netPatchData: unknown): number {
    if (!Array.isArray(netPatchData)) {
        return 0;
    }
    return netPatchData
        .slice(1)
        .reduce((count: number, entityOperations: unknown) => count + (Array.isArray(entityOperations) ? entityOperations.length - 1 : 0), 0);
}

/**
 * Summary of the local changes for the sync logs (never their content).
 */
export function describeTempPatch(tempPatch: TempPatch | null) {
    const attachmentUrls = Object.values(tempPatch?.patch?.attachmentManifest ?? {})
        .flatMap((entityAttachments) => Object.values(entityAttachments))
        .map((attachment) => attachment.url ?? '');
    const patchSet = tempPatch?.patchSet ?? [];

    return {
        'archipad.temp_patch.operations': countPatchOperations(tempPatch?.patch?.patch),
        'archipad.temp_patch.temp_attachments': attachmentUrls.filter((url) => url.startsWith('temp:')).length,
        'archipad.temp_patch.local_cache_attachments': attachmentUrls.filter((url) => url.startsWith('localCache:')).length,
        'archipad.temp_patch.base_patch_id': patchSet[patchSet.length - 1],
        'archipad.temp_patch.has_unflushed_changes': tempPatch?.hasUnflushedChanges,
        'archipad.temp_patch.problem': validateTempPatch(tempPatch) ?? undefined,
    } satisfies LogAttributes;
}

/** An attachment of a patch manifest stored in the temp patch folder (`temp:` url) */
export interface TempAttachmentReference {
    entityId: string;
    attachmentKey: string;
    url: string;
    file: PatchAttachmentDescriptor;
}

/**
 * Attachments of the manifest that are stored in the temp patch folder: the created or updated ones with a `temp:` url.
 * Deleted attachments and attachments pointing to a real patch folder (e.g. upgraded report entities) are ignored.
 */
export function listTempAttachments(manifest: NetPatchAttachmentManifest | null | undefined, serializer: AttachmentSerializer): TempAttachmentReference[] {
    const references: TempAttachmentReference[] = [];
    for (const entityId in manifest ?? {}) {
        const entityAttachments = manifest[entityId];
        for (const attachmentKey in entityAttachments) {
            const attachment = entityAttachments[attachmentKey];
            if (attachment.deleted) {
                continue;
            }
            const file = serializer.deserializeAttachment(attachment, entityId, attachmentKey);
            if (!(file instanceof PatchAttachmentDescriptor) || file.type !== 'temp') {
                continue;
            }
            references.push({ entityId, attachmentKey, url: attachment.url, file });
        }
    }
    return references;
}

/**
 * @param listedAttachmentPaths - `attachmentPath` of the files listed in the temp patch folder
 * @returns The temp attachments whose file is not in the temp patch folder
 */
export function findMissingTempAttachments(tempAttachments: readonly TempAttachmentReference[], listedAttachmentPaths: ReadonlySet<string>): TempAttachmentReference[] {
    return tempAttachments.filter((attachment) => !listedAttachmentPaths.has(attachment.file.attachmentPath));
}

/**
 * Removes the given attachments from the manifest, in place; an entity left without attachments is dropped from the manifest.
 * The entity keeps its other changes in the patch: it is published without that file.
 */
export function removeAttachmentsFromManifest(manifest: NetPatchAttachmentManifest, attachments: ReadonlyArray<Pick<TempAttachmentReference, 'entityId' | 'attachmentKey'>>): void {
    for (const { entityId, attachmentKey } of attachments) {
        const entityAttachments = manifest[entityId];
        if (!entityAttachments) {
            continue;
        }
        delete entityAttachments[attachmentKey];
        if (Object.keys(entityAttachments).length === 0) {
            delete manifest[entityId];
        }
    }
}

/**
 * The local temp patch to keep when the project is rebased without discarding the local changes.
 *
 * A temp patch is a diff over its `patchSet`. When some of those patches are not in the cache and can't be downloaded anymore
 * (deleted remotely, e.g. replaced dump patch), the project can't be rebuilt on that base: the temp patch is moved onto the
 * patches that are available, which is the same as publishing after the patches of the other users. The changes are never dropped.
 *
 * @param availablePatches - Ids of the patches in the cache, once the missing ones have been downloaded
 * @returns A new temp patch when its `patchSet` had to be moved, the same one otherwise
 */
export function keepTempPatch(tempPatch: TempPatch, availablePatches: Iterable<string>): { tempPatch: TempPatch; missingPatches: string[] } {
    const available = new Set(availablePatches);
    const missingPatches = tempPatch.patchSet.filter((patchId) => !available.has(patchId));
    if (missingPatches.length === 0) {
        return { tempPatch, missingPatches };
    }
    return {
        tempPatch: { ...tempPatch, patchSet: Array.from(available).sort(comparePatchId) },
        missingPatches,
    };
}
